import React, { useEffect, useState } from 'react';
import axios from 'axios';
import ProductCard from '../components/ProductCard';
import { ArrowRight, Sparkles } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Home = ({ setCurrentAudio }) => {
    const [featuredProducts, setFeaturedProducts] = useState([]);
    const [allProducts, setAllProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [wishlistIds, setWishlistIds] = useState([]);
    const { isAuthenticated } = useAuth();

    useEffect(() => {
        fetchProducts();
        if (isAuthenticated) {
            fetchWishlist();
        }
    }, [isAuthenticated]);

    const fetchProducts = async () => {
        try {
            const [featuredRes, allRes] = await Promise.all([
                axios.get(`${API}/products?featured=true`),
                axios.get(`${API}/products`)
            ]);
            setFeaturedProducts(featuredRes.data);
            setAllProducts(allRes.data.slice(0, 8));
        } catch (error) {
            console.error('Error fetching products:', error);
            toast.error('Failed to load products');
        } finally {
            setLoading(false);
        }
    };

    const fetchWishlist = async () => {
        try {
            const response = await axios.get(`${API}/wishlist`);
            setWishlistIds(response.data.products.map(p => p.id));
        } catch (error) {
            console.error('Error fetching wishlist:', error);
        }
    };

    const handleAddToCart = async (productId) => {
        try {
            await axios.post(`${API}/cart`, { product_id: productId });
            toast.success('Added to cart!');
        } catch (error) {
            toast.error('Failed to add to cart');
        }
    };

    const handleAddToWishlist = async (productId) => {
        try {
            if (wishlistIds.includes(productId)) {
                await axios.delete(`${API}/wishlist/${productId}`);
                setWishlistIds(wishlistIds.filter(id => id !== productId));
                toast.success('Removed from wishlist');
            } else {
                await axios.post(`${API}/wishlist/${productId}`);
                setWishlistIds([...wishlistIds, productId]);
                toast.success('Added to wishlist!');
            }
        } catch (error) {
            toast.error('Failed to update wishlist');
        }
    };

    const handlePlayAudio = (product) => {
        setCurrentAudio({
            url: product.audio_url,
            name: product.name
        });
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-xl text-muted-foreground">Loading...</div>
            </div>
        );
    }

    return (
        <div className="min-h-screen pt-16">
            {/* Hero Section */}
            <section className="hero-gradient min-h-screen flex items-center justify-center relative overflow-hidden" data-testid="hero-section">
                <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1617994452722-4145e196248b?w=1920')] bg-cover bg-center opacity-10"></div>
                <div className="px-6 md:px-12 lg:px-24 text-center relative z-10">
                    <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 border border-primary/20 rounded-full mb-8">
                        <Sparkles className="w-4 h-4 text-primary" />
                        <span className="text-sm font-mono uppercase tracking-wider text-primary">Premium Sound Library</span>
                    </div>
                    <h1 className="text-5xl md:text-7xl font-bold tracking-tighter mb-6 text-balance" data-testid="hero-title">
                        From Inspiration to Impact
                    </h1>
                    <p className="text-lg md:text-xl leading-relaxed text-muted-foreground mb-12 max-w-2xl mx-auto">
                        Sound resources for creators who build with conviction. You're called. What are you waiting for?
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                        <Link to="/browse" className="btn-primary" data-testid="browse-catalog-button">
                            Browse Catalog
                        </Link>
                        <Link to="/sample-packs" className="btn-secondary" data-testid="view-sample-packs-button">
                            View Sample Packs
                        </Link>
                    </div>
                </div>
            </section>

            {/* Featured Products - Bento Grid */}
            <section className="px-6 md:px-12 lg:px-24 py-24 md:py-32" data-testid="featured-section">
                <div className="flex items-center justify-between mb-12">
                    <div>
                        <h2 className="text-3xl md:text-5xl font-semibold tracking-tight mb-2">Featured Releases</h2>
                        <p className="text-lg text-muted-foreground">Handpicked by our producers</p>
                    </div>
                    <Link to="/browse" className="hidden md:flex items-center gap-2 text-primary hover:gap-4 transition-all">
                        <span className="text-sm uppercase tracking-widest font-mono">View All</span>
                        <ArrowRight className="w-5 h-5" strokeWidth={1.5} />
                    </Link>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
                    {featuredProducts.slice(0, 4).map((product, index) => (
                        <div 
                            key={product.id} 
                            className={index === 0 ? 'lg:col-span-2 lg:row-span-2' : ''}
                        >
                            <ProductCard 
                                product={product}
                                onAddToCart={handleAddToCart}
                                onAddToWishlist={handleAddToWishlist}
                                onPlayAudio={handlePlayAudio}
                                isInWishlist={wishlistIds.includes(product.id)}
                            />
                        </div>
                    ))}
                </div>
            </section>

            {/* All Products Grid */}
            <section className="px-6 md:px-12 lg:px-24 py-24 md:py-32 bg-secondary/50" data-testid="catalog-section">
                <h2 className="text-3xl md:text-5xl font-semibold tracking-tight mb-12">Latest Additions</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6 md:gap-8">
                    {allProducts.map((product) => (
                        <ProductCard 
                            key={product.id}
                            product={product}
                            onAddToCart={handleAddToCart}
                            onAddToWishlist={handleAddToWishlist}
                            onPlayAudio={handlePlayAudio}
                            isInWishlist={wishlistIds.includes(product.id)}
                        />
                    ))}
                </div>
                <div className="text-center mt-12">
                    <Link to="/browse" className="btn-primary" data-testid="view-all-products-button">
                        View All Products
                    </Link>
                </div>
            </section>
        </div>
    );
};

export default Home;
